/**
 * WalletOperationItem
 * Flutter source: wallet_screen.dart - _walletOperationItem()
 *
 * Displays recharge and withdraw wallet operations.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AdminUserAvatar } from './AdminUserAvatar';

interface WalletOperationItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  isFromAdmin?: boolean;
}

export const WalletOperationItem: React.FC<WalletOperationItemProps> = ({
  transaction,
  currentUserId,
  isFromAdmin = false,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const isRecharge = (transaction.walletInfo?.coins ?? 0) > 0;
  const isCurrentUser = transaction.usersInfo.userId === currentUserId;

  let title: string;
  let subTitle: string;
  let iconPath: string;

  if (isRecharge) {
    title = isCurrentUser ? l10n.wallet.recharged : l10n.wallet.someoneRecharged;
    subTitle = `${MathUtils.roundTwoDecimals(transaction.walletInfo?.coins ?? 0)} ${l10n.wallet.coins} · ${dateFormatters.formatDate(transaction.createdDate, true)}`;
    iconPath = '/assets/images/add.png';
  } else {
    if (transaction.walletInfo?.auto) {
      title = isCurrentUser ? l10n.wallet.autoWithdrawn : l10n.wallet.someoneAutoWithdrawn;
    } else {
      title = isCurrentUser ? l10n.wallet.withdrawn : l10n.wallet.someoneWithdrawn;
    }
    subTitle = `${MathUtils.roundTwoDecimals(transaction.walletInfo?.money ?? 0)} ${transaction.walletInfo?.currency ?? ''} · ${dateFormatters.formatDate(transaction.createdDate, true)}`;
    iconPath = '/assets/images/substract.png';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      {/* Circle icon container */}
      <div
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          backgroundColor: theme.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AssetIcon
          path={iconPath}
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.text.primary}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          {title}
        </div>
        <div style={{ color: theme.text.secondary, fontSize: responsive.font.xs, marginTop: responsive.gap.xs }}>
          {subTitle}
        </div>
      </div>

      {isFromAdmin && <AdminUserAvatar userId={transaction.usersInfo.userId} />}
    </div>
  );
};
